import { Suspense, lazy, ElementType, ComponentType } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// config
import {
  PATH_AFTER_AMBASSADOR_LOGIN,
  PATH_AFTER_LOGIN,
  PATH_AFTER_REFERRAL_LOGIN,
  PATH_AFTER_WAITER_LOGIN,
} from '../config/config';
// components
import LoadingScreen from '../components/LoadingScreen';
import LazyLoadErrorBoundary from '../components/LazyLoadErrorBoundary';
import { PATH_DASHBOARD } from './paths';
import useSettings from 'src/hooks/useSettings';
import { enhancedOrganizationsRoutes } from './enhanced-organizations.routes';
// onboarding utilities
import { needsOnboarding } from 'src/utils/onboardingTracker';
import { DomainType } from 'src/config/onboarding.config';

// ----------------------------------------------------------------------

// Retry a dynamic import up to 2 times before giving up.
// Handles stale chunk hashes after deployments.
function lazyWithRetry(
  importFn: () => Promise<{ default: ComponentType<any> }>
) {
  return lazy(() =>
    importFn().catch(() =>
      // First retry after a short delay (chunk might still be propagating)
      new Promise<{ default: ComponentType<any> }>((resolve) => setTimeout(resolve, 1500)).then(
        () => importFn()
      )
    )
  );
}

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <LazyLoadErrorBoundary>
      <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
        <Component {...props} />
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};

//const isShowTemplate = false;

export default function Router() {
  const { user } = useAuth();

  const { fieldType } = useSettings();

  // Map fieldType to DomainType for onboarding tracker
  const getDomainType = (): DomainType => {
    if (fieldType === 'event') return 'event';
    if (fieldType === 'restaurant') return 'restaurant';
    if (fieldType === 'musicStudio') return 'musicStudio';
    return 'event'; // default fallback
  };

  // Progressive onboarding check - user is new if they haven't completed required steps
  // Backward compatibility: Pass isNewUser to prevent re-onboarding old users
  const userNeedsOnboarding = needsOnboarding(user?.onBoardingSteps, getDomainType(), user?.isNewUser);
  
  const pathAfterLog = userNeedsOnboarding
    ? PATH_DASHBOARD.general.welcome
    : PATH_DASHBOARD.general.app;

  const pathHome = user?.role ? pathAfterLog : '/';

  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'access-request', element: <AccessRequest /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'complete-signup', element: <CompleteSignup /> },
        { path: 'accept-invitation', element: <AcceptInvitationPage /> },
        { path: 'complete-invitation', element: <CompleteInvitation /> },
        { path: 'public-invitation', element: <PublicInvitationPage /> },
      ],
    },

    {
      path: 'documentation',
      children: [

        {
          path: 'events',
          children: [
            {
              element: <Navigate to="/documentation/events/list" replace />,
              index: true,
            },
            { path: 'list', element: <EventsListPage /> },
            { path: 'userform', element: <FieldTypesDocumentation /> },
            { path: 'passculture', element: <PassCultureDocumentation /> },
            { path: 'physical-tickets', element: <PhysicalTicketsDocumentation /> },
            { path: 'invitation-tickets', element: <InvitationTicketsDocumentation /> },
            { path: 'promo-codes', element: <PromoCodesDocumentation /> },
            { path: 'ticket-categories', element: <TicketCategoriesDocumentation /> },
            { path: 'bus-management', element: <BusManagementDocumentation /> },
            { path: 'campaign-tracking', element: <CampaignTrackingDocumentation /> },
            { path: 'promobooster', element: <PromoBoosterDocumentation /> },
          ],
        },
        { path: 'shareboost', element: <ShareBoostDocumentation /> },
        { path: 'marketing', element: <MarketingSummaryDocumentation /> },
        {
          path: 'studios',
          children: [
            { path: 'payment-modes', element: <PaymentModeDocumentation /> },
            { path: 'notification-matrix', element: <BookingNotificationMatrix /> },
          ],
        },

      ],
    },


    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={pathAfterLog} replace />, index: true },
        //general
        { path: 'app', element: <GlobalDashboard /> },
        { path: 'welcome', element: <WelcomePage /> },
        { path: 'notifications', element: <PromotionRequest /> },

        // events
        {
          path: 'evenementiel',
          children: [
            { element: <Navigate to="/dashboard/evenementiel/orgs" replace />, index: true },
            {
              path: 'orgs',
              children: [
                {
                  element: <Navigate to="/dashboard/evenementiel/orgs/list" replace />,
                  index: true,
                },
                { path: 'list', element: <OrgsListPage /> },
                { path: 'new', element: <OrgAddPage /> },
                { path: ':id/view', element: <OrgViewPage /> },
                { path: ':id/edit', element: <OrgEditPage /> },
                { path: ':id/manage-services/*', element: <OrganizationServiceManagement /> },
              ],
            },
            {
              path: 'events',
              children: [
                {
                  element: <Navigate to="/dashboard/evenementiel/events/list" replace />,
                  index: true,
                },
                { path: 'list', element: <EventsListPage /> },
                { path: 'new', element: <EventAddPage /> },
                { path: 'analytics', element: <EventAnalytics /> },
                { path: 'attendees', element: <AttendeesAnalytics /> },
                { path: ':id/view', element: <EventViewPage /> },
                { path: ':id/dashboard', element: <EventDashboardPage /> },
                { path: ':id/preview', element: <EventPreviewPage /> },
                { path: ':id/edit', element: <EventEditPage /> },
                { path: ':id/create', element: <EventFullCreatePage /> },
                { path: ':id/seatmapper', element: <SeatMapper /> },
                { path: ':id/reservations', element: <EventReservationsPage /> },
                { path: ':id/event-stats', element: <EventStats /> },
              ],
            },
            {
              path: 'ambassador-events',
              children: [
                {
                  element: <Navigate to="/dashboard/evenementiel/ambassador-events/list" replace />,
                  index: true,
                },
                { path: 'list', element: <AmbassadorEvents /> },
                { path: ':id/event-dashborad', element: <AmbassadorEventDashboard /> },
              ],
            },
            {
              path: 'search-for-events',
              element: <SearchForEvents />,
            },
            {
              path: 'my-events',
              children: [
                {
                  element: <Navigate to="/dashboard/evenementiel/my-events/list" replace />,
                  index: true,
                },
                { path: 'list', element: <RpEventsList /> },
                { path: ':id/event-stats', element: <EventStats /> },
                { path: ':id/event-details', element: <EventDetailsPage /> },
              ],
            },
          ],
        },
        // studios
        {
          path: 'studios',
          children: [
            { element: <Navigate to="/dashboard/studios/list" replace />, index: true },
            {
              path: 'list',
              element: <OrgsListPage />, // Reuse the same component but filter for studios
            },
            {
              path: 'new',
              element: <OrgAddPage />, // Reuse the same component but default to studio type
            },
            {
              path: ':id/view',
              element: <OrgViewPage />, // Reuse the same component
            },
            {
              path: ':id/edit',
              element: <OrgEditPage />, // Reuse the same component
            },
            {
              path: ':id/manage-services/*',
              element: <OrganizationServiceManagement />, // This is the main studio management interface
            },
          ],
        },
        //restauration
        {
          path: 'restauration',
          children: [
            { element: <Navigate to="/dashboard/restauration/restaurants" replace />, index: true },
            {
              path: 'restaurants',
              children: [
                {
                  element: <Navigate to="/dashboard/restauration/restaurants/list" replace />,
                  index: true,
                },
                { path: 'list', element: <RessListPage /> },
                { path: 'new', element: <ResAddPage /> },
                { path: ':id/view', element: <ResViewPage /> },
                { path: ':id/edit', element: <ResEditPage /> },
              ],
            },
            { path: 'commandes', element: <OrdersListPage /> },
            { path: 'stores', element: <StoresPage /> },
            { path: 'store-list', element: <StoresListPage /> },
            { path: 'store-create', element: <StoreCreate /> },
            { path: 'orders', element: <EventsOrders /> },
            {
              path: 'applications',
              children: [
                {
                  element: <Navigate to="/dashboard/restauration/applications/list" replace />,
                  index: true,
                },
                { path: 'list', element: <ApplicationListPage /> },
                { path: 'zelty', element: <ZeltyPage /> },
              ],
            },
            {
              path: 'menus',
              children: [
                {
                  element: <Navigate to="/dashboard/restauration/menus/list" replace />,
                  index: true,
                },
                { path: 'list', element: <MenusListPage /> },
                { path: 'new', element: <MenuAdd /> },
                { path: 'menu-details/:id/view', element: <MenuView /> },
              ],
            },
            { path: 'tables-list', element: <WaitersTablesList /> },
          ],
        },
        //network
        {
          path: 'network',
          children: [
            { element: <Navigate to="/dashboard/network/invite-users" replace />, index: true },
            { path: 'invite-users', element: <InviteUsersPage /> },
            { path: 'invite-agents', element: <InviteAgentsPage /> },
            { path: 'invite-staff', element: <InviteStaffPage /> },
            { path: 'invitations-list', element: <InvitationsListPage /> },
            { path: 'promoters-tree', element: <PromotersTreePage /> },
            { path: 'staff-list', element: <StaffListPage /> },
            { path: 'ambassador-invites', element: <AmbasInvitesListPage invitingUser={true} /> },
            { path: 'promotion-requests', element: <OrgsRequests /> },
            { path: ':id/referral-details', element: <ReferralDetails /> },
            { path: ':id/owner-details', element: <OwnerDetails /> },
            { path: 'promotion-groups', element: <PromotionGroupsPage /> },
          ],
        },
        // user
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            { path: 'new', element: <UserCreate /> },
            { path: ':name/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
            { path: 'billing', element: <BillingPage /> },
            { path: 'seatmapper', element: <SeatMapper /> },
            { path: 'onboarding', element: <></> },
            { path: 'mails', element: <UserMails /> },
            { path: 'contacts', element: <UserContacts /> },
            { path: ':id/mailedit', element: <MailEditor /> },
            // { path: 'delete-account', element: <DeleteAccount /> },
          ],
        },
        // more
        { path: 'calendar', element: <Calendar /> },
        { path: 'kanban', element: <Kanban /> },
        { path: 'permission-denied', element: <PermissionDenied /> },
        {
          path: 'invitations',
          children: [
            { element: <Navigate to="/dashboard/invitations/list" replace />, index: true },
            { path: 'list', element: <OrgsRequests /> },
            { path: ':id/referral-details', element: <ReferralDetails /> },
          ],
        },
        
        // Enhanced Organizations
        enhancedOrganizationsRoutes,
        
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: 'privacy-policy', element: <PrivacyPolicy /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
        { path: 'delete-account', element: <DeleteAccount /> },
      ],
    },
    {
      path: '/',
      element: (
        <>
          <MainLayout />
          <HomePage />
        </>
      ),
      children: [
        { element: <Navigate to={pathHome} replace />, index: true },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazyWithRetry(() => import('../pages/auth/Login')));
const Register = Loadable(lazyWithRetry(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazyWithRetry(() => import('../pages/auth/ResetPassword')));
const AccessRequest = Loadable(lazyWithRetry(() => import('../pages/auth/AccessRequest')));
const NewPassword = Loadable(lazyWithRetry(() => import('../pages/auth/NewPassword')));
const CompleteSignup = Loadable(lazyWithRetry(() => import('../pages/auth/completeSignup')));
const AcceptInvitationPage = Loadable(lazyWithRetry(() => import('../pages/auth/acceptInvitationPage')));
const CompleteInvitation = Loadable(lazyWithRetry(() => import('../pages/auth/completeInvitationPage')));
const PublicInvitationPage = Loadable(lazyWithRetry(() => import('../pages/auth/publicInvitationPage')));

// DASHBOARD

// GENERAL

export const UserCardsPage = Loadable(lazyWithRetry(() => import('../pages/z_more/UserCards')));
//Events
const EventAddPage = Loadable(lazyWithRetry(() => import('../pages/events/eventAddPage')));
const EventEditPage = Loadable(lazyWithRetry(() => import('../pages/events/eventEditPage')));
const EventDashboardPage = Loadable(lazyWithRetry(() => import('../pages/events/eventDashboardPage')));
const EventPreviewPage = Loadable(lazyWithRetry(() => import('../pages/events/eventPreviewPage')));
const EventFullCreatePage = Loadable(lazyWithRetry(() => import('../pages/events/eventFullCreatePage')));
const EventViewPage = Loadable(lazyWithRetry(() => import('../pages/events/eventViewPage')));
const EventsListPage = Loadable(lazyWithRetry(() => import('../pages/events/eventsListPage')));
const RpEventsList = Loadable(lazyWithRetry(() => import('../pages/events/rpEventsList')));
const EventReservationsPage = Loadable(lazyWithRetry(() => import('../pages/events/eventReservationPage')));
const EventStats = Loadable(lazyWithRetry(() => import('../pages/events/eventStats')));
const EventDetailsPage = Loadable(lazyWithRetry(() => import('../pages/events/eventDetailsPage')));
const SearchForEvents = Loadable(lazyWithRetry(() => import('../pages/events/searchForEvents')));
const ReferralDetails = Loadable(
  lazy(() => import('../pages/organisations/requests/referralDetails'))
);
const OwnerDetails = Loadable(lazyWithRetry(() => import('../pages/organisations/requests/ownerDetails')));
const PromotionGroupsPage = Loadable(lazyWithRetry(() => import('../pages/network/PromotionGroupsPage')));
const AmbassadorEvents = Loadable(lazyWithRetry(() => import('../pages/events/ambassadorEventsPage')));
const AmbasInvitesListPage = Loadable(
  lazy(() => import('../pages/ambassador/invites/ambasInvitesListPage'))
);
const AmbassadorEventDashboard = Loadable(
  lazy(() => import('../pages/events/ambassadorEventDashboard'))
);
const EventsOrders = Loadable(lazyWithRetry(() => import('../pages/events/eventsOrdersPage')));
const BillingPage = Loadable(lazyWithRetry(() => import('../pages/BillingPage')));

// ORG
const OrgAddPage = Loadable(lazyWithRetry(() => import('../pages/organisations/orgAddPage')));
const OrgEditPage = Loadable(lazyWithRetry(() => import('../pages/organisations/orgEditPage')));
const OrgViewPage = Loadable(lazyWithRetry(() => import('../pages/organisations/orgViewPage')));
const OrgsListPage = Loadable(lazyWithRetry(() => import('../pages/organisations/orgsListPage')));
const OrgsRequests = Loadable(lazyWithRetry(() => import('../pages/organisations/orgsRequestsPage')));
const OrganizationServiceManagement = Loadable(lazyWithRetry(() => import('../pages/organisations/service-management/OrganizationServiceManagement')));

// Restaurant
const ResAddPage = Loadable(lazyWithRetry(() => import('../pages/restaurants/orgAddPage')));
const ResEditPage = Loadable(lazyWithRetry(() => import('../pages/restaurants/orgEditPage')));
const ResViewPage = Loadable(lazyWithRetry(() => import('../pages/restaurants/orgViewPage')));
const RessListPage = Loadable(lazyWithRetry(() => import('../pages/restaurants/orgsListPage')));
// const RessRequests = Loadable(lazyWithRetry(() => import('../pages/restaurants/orgsRequestsPage')));
const MenusListPage = Loadable(lazyWithRetry(() => import('../pages/restaurants/menus/menusListPage')));
const MenuAdd = Loadable(lazyWithRetry(() => import('../pages/restaurants/menus/menuAdd')));
const MenuView = Loadable(lazyWithRetry(() => import('../pages/restaurants/menus/menuView')));
const WaitersTablesList = Loadable(
  lazy(() => import('../pages/restaurants/waitersTables/waitersTablesList'))
);

//welcome page
const WelcomePage = Loadable(lazyWithRetry(() => import('../pages/welcome/welcome')));
//global dashboard
const GlobalDashboard = Loadable(lazyWithRetry(() => import('../pages/dashboard/GlobalDashboard')));
//Stores
const StoresPage = Loadable(lazyWithRetry(() => import('../pages/stores/storesPage')));
//Stores list
const StoresListPage = Loadable(lazyWithRetry(() => import('../pages/storeList/storesListPage')));
//Stores create
const StoreCreate = Loadable(lazyWithRetry(() => import('../pages/stores/createStore')));
// Orders
const OrdersListPage = Loadable(lazyWithRetry(() => import('../pages/orders/ordersPage')));
// Applications
const ApplicationListPage = Loadable(lazyWithRetry(() => import('../pages/application/applicationPage')));
const ZeltyPage = Loadable(lazyWithRetry(() => import('../pages/application/Apps/zelty')));

const InviteUsersPage = Loadable(lazyWithRetry(() => import('../pages/network/inviteUsersPage')));
const InviteAgentsPage = Loadable(lazyWithRetry(() => import('../pages/network/inviteAgentsPage')));
const InviteStaffPage = Loadable(lazyWithRetry(() => import('../pages/network/inviteStaffPage')));
const InvitationsListPage = Loadable(lazyWithRetry(() => import('../pages/network/invitationsListPage')));
const PromotersTreePage = Loadable(
  lazy(() => import('../sections/@dashboard/network/PromotersTree'))
);
const StaffListPage = Loadable(lazyWithRetry(() => import('../sections/@dashboard/network/StaffList')));
const PromotionRequest = Loadable(
  lazy(() => import('../pages/organisations/requests/promotionRequest'))
);

// Documentation
const FieldTypesDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/FieldTypesDocumentation')));
const PassCultureDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/passculture/PassCultureDocumentation')));
const ShareBoostDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/ShareBoostDocumentation')));
const PaymentModeDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/studios/PaymentModeDocumentation')));
const PhysicalTicketsDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/PhysicalTicketsDocumentation')));
const BookingNotificationMatrix = Loadable(lazyWithRetry(() => import('../sections/documentation/studios/BookingNotificationMatrix')));
const InvitationTicketsDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/InvitationTicketsDocumentation')));
const PromoCodesDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/PromoCodesDocumentation')));
const TicketCategoriesDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/TicketCategoriesDocumentation')));
const BusManagementDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/BusManagementDocumentation')));
const CampaignTrackingDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/CampaignTrackingDocumentation')));
const PromoBoosterDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/PromoBoosterDocumentation')));
const MarketingSummaryDocumentation = Loadable(lazyWithRetry(() => import('../sections/documentation/events/MarketingSummaryDocumentation')));

/* const EcommerceProductEdit = Loadable(
  lazy(() => import('../pages/dashboard/EcommerceProductEdit'))
); */

// INVOICE

// BLOG

// USER

const UserAccount = Loadable(lazyWithRetry(() => import('../pages/UserAccount')));
const UserMails = Loadable(lazyWithRetry(() => import('../pages/Mails')));
const EventAnalytics = Loadable(lazyWithRetry(() => import('../sections/@dashboard/events/analytics/EventAnalytics')));
const AttendeesAnalytics = Loadable(lazyWithRetry(() => import('../sections/@dashboard/events/analytics/AttendeesAnalytics')));
const UserContacts = Loadable(lazyWithRetry(() => import('../sections/@dashboard/contacts/ContactsManager')));
const MailEditor = Loadable(lazyWithRetry(() => import('../pages/MailEditor')));
const UserCreate = Loadable(lazyWithRetry(() => import('../pages/z_more/UserCreate')));
const DeleteAccount = Loadable(lazyWithRetry(() => import('../pages/DeleteAccount')));
const SeatMapper = Loadable(lazyWithRetry(() => import('../pages/SeatMapper')));
// APP

const Calendar = Loadable(lazyWithRetry(() => import('../pages/calendar/CalendarPage')));
const Kanban = Loadable(lazyWithRetry(() => import('../pages/z_more/Kanban')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazyWithRetry(() => import('../pages/PermissionDenied')));

// MAIN
const HomePage = Loadable(lazyWithRetry(() => import('../pages/Home')));
const About = Loadable(lazyWithRetry(() => import('../pages/About')));
const Contact = Loadable(lazyWithRetry(() => import('../pages/Contact')));
const PrivacyPolicy = Loadable(lazyWithRetry(() => import('../pages/PrivacyPolicy')));
const Faqs = Loadable(lazyWithRetry(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazyWithRetry(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazyWithRetry(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazyWithRetry(() => import('../pages/Pricing')));
const Payment = Loadable(lazyWithRetry(() => import('../pages/Payment')));
const Page500 = Loadable(lazyWithRetry(() => import('../pages/Page500')));
const Page403 = Loadable(lazyWithRetry(() => import('../pages/Page403')));
const Page404 = Loadable(lazyWithRetry(() => import('../pages/Page404')));

// Enhanced Organizations
const EnhancedOrganizationsListPage = Loadable(
  lazy(() => import('../pages/organizations/enhanced/EnhancedOrganizationsListPage'))
);
